import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import Form from "src/components/base/form";
import {
    Description,
    ErrorMessage,
    Field,
    FieldGroup,
    Fieldset,
    Label,
    Legend,
    RequiredLabel,
} from "src/components/base/fieldset";
import { useForm, useStore } from "@tanstack/react-form";
import React from "react";
import { ApiInternal } from "src/api/api_internal";
import { toast } from "react-toastify";
import {
    SearchResultsEntry,
    SetLdapQuerySettingsRequest,
    TestLdapSearchResponse,
} from "src/api/generated/frontend_internal_v1";
import { Divider } from "src/components/base/divider";
import { Button, PrimaryButton } from "src/components/base/button";
import { Input } from "src/components/base/input";
import BlockUnsavedChanges from "src/components/base/unsaved-changes";
import { Radio, RadioField, RadioGroup } from "src/components/base/radio";
import { Subheading } from "src/components/base/heading";
import { Textarea } from "src/components/base/textarea";
import { AnimatePresence, motion } from "framer-motion";
import { Text } from "src/components/base/text";
import {
    ChevronDownIcon,
    ExclamationTriangleIcon,
    EyeIcon,
    EyeSlashIcon,
    UserGroupIcon,
    UsersIcon,
} from "@heroicons/react/20/solid";
import { Badge } from "src/components/base/badges/base";
import { clsx } from "clsx";
import { ListErrorBox, SimpleErrorBox } from "src/components/errorBox";
import { Switch } from "src/components/base/switch";

/**
 * The properties for {@link SettingLdap}
 */
export type SettingLdapProps = {};

/**
 * Setting page to configure Ldap
 */
function SettingLdap(props: SettingLdapProps) {
    const [t] = useTranslation("admin-settings");
    const [tg] = useTranslation();
    const [testResult, setTestResult] = React.useState<TestLdapSearchResponse>();
    const [groupIndex, setGroupIndex] = React.useState<number>();
    const [usersOpen, setUsersOpen] = React.useState<boolean>(false);
    const [showPassword, setShowPassword] = React.useState(false);

    const [pendingSettings, setPendingSettings] = React.useState<SetLdapQuerySettingsRequest>();
    const router = useRouter();
    const settings = Route.useLoaderData();

    const queryForm = useForm({
        defaultValues: {
            group_base: settings ? settings.group_base : "",
            group_filter: settings ? settings.group_filter : "",
            group_name: settings ? settings.group_name : "",
            membership_attr: settings ? settings.membership_attr : "",
            membership_in_group: settings ? settings.membership_in_group : true,
            user_base: settings ? settings.user_base : "",
            user_filter: settings ? settings.user_filter : "",
            user_name: settings ? settings.user_name : "",
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                const settings = {
                    group_base: value.group_base,
                    group_name: value.group_name,
                    group_filter: value.group_filter,
                    membership_in_group: value.membership_in_group,
                    membership_attr: value.membership_attr,
                    user_base: value.user_base,
                    user_name: value.user_name,
                    user_filter: value.user_filter,
                };

                const res = await ApiInternal.ldap.testSettings(settings);
                setPendingSettings(settings);

                if (res.result === "Err") {
                    setTestResult(undefined);
                    return {
                        fields: {
                            group_filter: res.error.group_filter_invalid
                                ? t("description.group-filter-err")
                                : undefined,
                            user_filter: res.error.user_filter_invalid ? t("description.user-filter-err") : undefined,
                        },
                    };
                }

                setTestResult(res.value);
            },
        },
    });
    const isDirty = useStore(queryForm.store, (store) => store.isDirty);

    const connectionForm = useForm({
        defaultValues: {
            ldap_enabled: settings ? settings.ldap_enabled : false,
            ldap_url: settings ? settings.ldap_url : "",
            ldap_user: settings ? settings.ldap_user : "",
            ldap_password: settings ? settings.ldap_password : "",
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                const res = await ApiInternal.ldap.setConnectionSettings({
                    ldap_enabled: value.ldap_enabled,
                    ldap_password: value.ldap_password,
                    ldap_url: value.ldap_url,
                    ldap_user: value.ldap_user,
                });

                if (res.result === "Err") {
                    return {
                        fields: {
                            ldap_url: res.error.url_invalid ? t("error.url-invalid") : undefined,
                        },
                    };
                }
            },
        },
        onSubmit: () => {
            toast.success("Saved successfully");
            connectionForm.reset();
            router.invalidate({ sync: true });
        },
    });
    const connectionIsDirty = useStore(connectionForm.store, (store) => store.isDirty);
    const ldapEnabled = useStore(connectionForm.store, (store) => store.values.ldap_enabled);

    /**
     * Count in how many groups a user is
     *
     * @param user user
     * @param groups all groups
     * @returns number of groups
     */
    const userInNumberOfGroups = (user: string, groups: { [key: string]: SearchResultsEntry | undefined }): number => {
        let count = 0;

        Object.values(groups).forEach((value) => {
            if (value) {
                value.list.forEach((member) => {
                    if (member === user) {
                        count++;
                    }
                });
            }
        });

        return count;
    };

    /**
     * Get an array of html list elements of users in a specific group
     *
     * @param group group
     * @param users all users
     * @returns JSX.Element list
     */
    const getUsersInGroup = (
        group: string,
        users: {
            [key: string]: SearchResultsEntry | undefined;
        },
    ): JSX.Element[] => {
        const results: JSX.Element[] = [];
        Object.entries(users).forEach(([key, value]) => {
            if (value) {
                value.list.forEach((inGroup) => {
                    if (inGroup === group) {
                        results.push(<li>{key}</li>);
                    }
                });
            }
        });
        return results;
    };

    /**
     * Api call to save the Ldap settings
     */
    const saveSettings = async () => {
        if (!pendingSettings) {
            return;
        }
        const res = await ApiInternal.ldap.setQuerySettings(pendingSettings);
        if (res.result === "Ok") {
            toast.success(tg("toast.saved-successfully"));
            setTestResult(undefined);
            setPendingSettings(undefined);

            queryForm.reset();
            router.invalidate({ sync: true });
        } else {
            if (res.error.group_filter_invalid !== null && res.error.group_filter_invalid !== undefined) {
                toast.error(t("description.group-filter-err"));
            }
            if (res.error.user_filter_invalid !== null && res.error.user_filter_invalid !== undefined) {
                toast.error(t("description.user-filter-err"));
            }
        }
    };

    return (
        <div className={"flex h-full w-full flex-col justify-start gap-3"}>
            <Form className={"flex max-w-lg flex-col gap-6"} onSubmit={connectionForm.handleSubmit}>
                <Fieldset className={"max-w-lg"}>
                    <Legend>{t("heading.connection-settings")}</Legend>
                    <FieldGroup>
                        <connectionForm.Field name={"ldap_enabled"}>
                            {(fieldApi) => (
                                <Field className={"flex justify-between"}>
                                    <div>
                                        <Label>{t("label.enable-ldap")}</Label>
                                    </div>
                                    <Switch
                                        checked={fieldApi.state.value}
                                        onChange={(e) => {
                                            fieldApi.handleChange(e);
                                        }}
                                        name={"enable_file_sharing"}
                                    />
                                </Field>
                            )}
                        </connectionForm.Field>
                    </FieldGroup>
                </Fieldset>
                <Fieldset className={"max-w-lg"} disabled={!ldapEnabled}>
                    <FieldGroup>
                        <connectionForm.Field name={"ldap_url"}>
                            {(fieldApi) => (
                                <Field>
                                    <RequiredLabel>{t("label.ldap-url")}</RequiredLabel>
                                    <Description>{t("description.ldap-url")}</Description>
                                    <Input
                                        required={true}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                    {fieldApi.state.meta.errors.map((err) => (
                                        <ErrorMessage>{err}</ErrorMessage>
                                    ))}
                                </Field>
                            )}
                        </connectionForm.Field>
                        <connectionForm.Field name={"ldap_user"}>
                            {(fieldApi) => (
                                <Field>
                                    <RequiredLabel>{t("label.ldap-user")}</RequiredLabel>
                                    <Description>{t("description.ldap-user")}</Description>

                                    <Input
                                        required={true}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                </Field>
                            )}
                        </connectionForm.Field>
                        <connectionForm.Field name={"ldap_password"}>
                            {(fieldApi) => (
                                <Field>
                                    <RequiredLabel>{t("label.ldap-password")}</RequiredLabel>
                                    <Description>{t("description.ldap-password")}</Description>
                                    <div className={"mt-3 flex gap-3"}>
                                        <Input
                                            required={true}
                                            value={fieldApi.state.value}
                                            type={showPassword ? "text" : "password"}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />
                                        <Button plain={true} onClick={() => setShowPassword(!showPassword)}>
                                            {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                                        </Button>
                                    </div>
                                </Field>
                            )}
                        </connectionForm.Field>
                    </FieldGroup>
                </Fieldset>
                <Divider />
                <div className={"flex justify-end gap-3"}>
                    <Button plain={true} disabled={!connectionIsDirty} onClick={() => connectionForm.reset()}>
                        {t("button.reset")}
                    </Button>
                    <PrimaryButton type="submit"> {t("button.save")}</PrimaryButton>
                </div>
                <BlockUnsavedChanges condition={connectionIsDirty} />
            </Form>
            <Divider className={"my-3"} />

            <AnimatePresence>
                {!testResult && (
                    <motion.div transition={{ duration: 0.3 }} exit={{ opacity: 0 }}>
                        <Form onSubmit={queryForm.handleSubmit}>
                            <Fieldset disabled={settings ? !settings.ldap_enabled : true} className={"max-w-lg"}>
                                <Legend>{t("heading.query-settings")}</Legend>
                                <FieldGroup>
                                    <queryForm.Field name={"group_base"}>
                                        {(fieldApi) => (
                                            <Field>
                                                <RequiredLabel>{t("label.group-base")}</RequiredLabel>
                                                <Description>{t("description.group-base")}</Description>
                                                <Input
                                                    required={true}
                                                    autoFocus={true}
                                                    value={fieldApi.state.value}
                                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                                />
                                            </Field>
                                        )}
                                    </queryForm.Field>
                                    <queryForm.Field name={"group_filter"}>
                                        {(fieldApi) => (
                                            <Field>
                                                <RequiredLabel>{t("label.group-filter")}</RequiredLabel>
                                                <Description>{t("description.group-filter")}</Description>
                                                <Input
                                                    required={true}
                                                    value={fieldApi.state.value}
                                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                                    invalid={fieldApi.state.meta.errors.length > 0}
                                                />
                                                {fieldApi.state.meta.errors.map((err) => (
                                                    <ErrorMessage>{err}</ErrorMessage>
                                                ))}
                                            </Field>
                                        )}
                                    </queryForm.Field>
                                    <queryForm.Field name={"group_name"}>
                                        {(fieldApi) => (
                                            <Field>
                                                <RequiredLabel>{t("label.group-name")}</RequiredLabel>
                                                <Input
                                                    required={true}
                                                    value={fieldApi.state.value}
                                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                                />
                                            </Field>
                                        )}
                                    </queryForm.Field>
                                    <Divider />
                                    <queryForm.Field name={"membership_in_group"}>
                                        {(fieldApi) => (
                                            <Field>
                                                <Label>{t("label.membership-in")}</Label>
                                                <RadioGroup
                                                    value={fieldApi.state.value ? "group" : "user"}
                                                    onChange={(value) => fieldApi.handleChange(value === "group")}
                                                    aria-label="Membership group user"
                                                >
                                                    <RadioField>
                                                        <Radio value="group" />
                                                        <Label> {t("label.group")}</Label>
                                                        <Description>
                                                            {t("description.membership-defined-in-group")}
                                                        </Description>
                                                    </RadioField>
                                                    <RadioField>
                                                        <Radio value="user" />
                                                        <Label>{t("label.user")}</Label>
                                                        <Description>
                                                            {t("description.membership-defined-in-user")}
                                                        </Description>
                                                    </RadioField>
                                                </RadioGroup>
                                            </Field>
                                        )}
                                    </queryForm.Field>
                                    <queryForm.Field name={"membership_attr"}>
                                        {(fieldApi) => (
                                            <Field>
                                                <RequiredLabel>{t("label.member-attr")}</RequiredLabel>
                                                <Description>{t("description.member-attr")}</Description>
                                                <Input
                                                    required={true}
                                                    value={fieldApi.state.value}
                                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                                />
                                            </Field>
                                        )}
                                    </queryForm.Field>
                                    <Divider />
                                    <queryForm.Field name={"user_base"}>
                                        {(fieldApi) => (
                                            <Field>
                                                <RequiredLabel>{t("label.user-base")}</RequiredLabel>
                                                <Description>{t("description.user-base")}</Description>
                                                <Input
                                                    required={true}
                                                    value={fieldApi.state.value}
                                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                                />
                                            </Field>
                                        )}
                                    </queryForm.Field>
                                    <queryForm.Field name={"user_filter"}>
                                        {(fieldApi) => (
                                            <Field>
                                                <RequiredLabel>{t("label.user-filter")}</RequiredLabel>
                                                <Description>{t("description.user-filter")}</Description>
                                                <Input
                                                    required={true}
                                                    value={fieldApi.state.value}
                                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                                    invalid={fieldApi.state.meta.errors.length > 0}
                                                />
                                                {fieldApi.state.meta.errors.map((err) => (
                                                    <ErrorMessage>{err}</ErrorMessage>
                                                ))}
                                            </Field>
                                        )}
                                    </queryForm.Field>
                                    <queryForm.Field name={"user_name"}>
                                        {(fieldApi) => (
                                            <Field>
                                                <RequiredLabel>{t("label.user-name")}</RequiredLabel>
                                                <Description>{t("description.user-name")}</Description>
                                                <Input
                                                    required={true}
                                                    value={fieldApi.state.value}
                                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                                />
                                            </Field>
                                        )}
                                    </queryForm.Field>
                                    <Divider />
                                    <div className={"flex justify-end gap-3"}>
                                        <Button plain={true} disabled={!isDirty} onClick={() => queryForm.reset()}>
                                            {t("button.reset")}
                                        </Button>
                                        <PrimaryButton type="submit"> {t("button.test")}</PrimaryButton>
                                    </div>
                                </FieldGroup>
                            </Fieldset>
                            <BlockUnsavedChanges condition={isDirty} />
                        </Form>
                    </motion.div>
                )}
            </AnimatePresence>

            {testResult && (
                <>
                    <Button className={"self-start"} outline={true} onClick={() => setTestResult(undefined)}>
                        {t("button.change-settings")}
                    </Button>

                    <div className={"flex flex-col gap-8"}>
                        <Subheading>{t("heading.result")}</Subheading>
                        {testResult.type === "Success" && (
                            <div className={"flex w-full flex-col gap-8"}>
                                {testResult.long_names && Object.entries(testResult.long_names).length > 0 && (
                                    <ListErrorBox
                                        heading={t("heading.long-names")}
                                        description={t("description.long-names")}
                                        data={testResult.long_names}
                                    />
                                )}

                                {testResult.invalid_names && Object.entries(testResult.invalid_names).length > 0 && (
                                    <ListErrorBox
                                        heading={t("heading.invalid-names")}
                                        description={t("description.invalid-names")}
                                        data={testResult.invalid_names}
                                    />
                                )}

                                {testResult.missing_names.length > 0 && (
                                    <SimpleErrorBox
                                        data={testResult.missing_names}
                                        heading={t("heading.missing-names")}
                                        description={t("description.missing-names")}
                                    />
                                )}
                                {testResult.mutliple_names.length > 0 && (
                                    <SimpleErrorBox
                                        data={testResult.mutliple_names}
                                        heading={t("heading.multiple-names")}
                                    />
                                )}

                                {testResult.long_dns.length > 0 && (
                                    <SimpleErrorBox
                                        data={testResult.long_dns}
                                        heading={t("heading.long-dns")}
                                        description={t("description.long-dns")}
                                    />
                                )}
                                {testResult.invalid_dns.length > 0 && (
                                    <SimpleErrorBox
                                        data={testResult.invalid_dns}
                                        heading={t("heading.invalid-dns")}
                                        description={t("description.invalid-dns")}
                                    />
                                )}

                                {testResult.groups && (
                                    <div className={"flex w-full flex-col gap-3"}>
                                        <Subheading>{t("heading.groups-found")}</Subheading>
                                        {Object.entries(testResult.groups).map(([groupKey, value], index) => (
                                            <motion.div
                                                layout
                                                transition={{
                                                    duration: 0.3,
                                                }}
                                                onClick={() => {
                                                    if (groupIndex === index) {
                                                        setGroupIndex(undefined);
                                                    } else {
                                                        setGroupIndex(index);
                                                    }
                                                }}
                                                className={clsx(
                                                    "rounded border border-zinc-950/5 bg-zinc-400/10 p-3 dark:border-white/5",
                                                    value && value.list.length > 0 ? "hover:cursor-pointer" : "",
                                                )}
                                            >
                                                <motion.div layout className={"flex justify-between gap-3"}>
                                                    <div className={"flex gap-3"}>
                                                        <div className={"w-5 *:fill-black/40 *:dark:fill-white/40"}>
                                                            <UserGroupIcon />
                                                        </div>
                                                        <Subheading>{value?.name}</Subheading>
                                                        {pendingSettings?.membership_in_group ? (
                                                            <Badge color={"zinc"}>{value?.list.length}</Badge>
                                                        ) : (
                                                            <Badge color={"zinc"}>
                                                                {getUsersInGroup(groupKey, testResult.users).length}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    {value && value.list.length > 0 && (
                                                        <div className={"w-5 *:fill-black/40 *:dark:fill-white/40"}>
                                                            <ChevronDownIcon />
                                                        </div>
                                                    )}
                                                </motion.div>
                                                <motion.div layout>
                                                    <div className={groupIndex === index ? "" : "hidden"}>
                                                        <>
                                                            {pendingSettings?.membership_in_group ? (
                                                                value &&
                                                                value.list.length > 0 && (
                                                                    <>
                                                                        <Text>{t("heading.users")}</Text>
                                                                        <ul
                                                                            className={
                                                                                "mt-2 ml-4 list-disc text-base/6 text-zinc-500 sm:text-sm/6 dark:text-zinc-400"
                                                                            }
                                                                        >
                                                                            {value.list.map((user) => (
                                                                                <li>{user}</li>
                                                                            ))}
                                                                        </ul>
                                                                    </>
                                                                )
                                                            ) : getUsersInGroup(groupKey, testResult.users).length >
                                                              0 ? (
                                                                <>
                                                                    <Text>{t("heading.users")}</Text>
                                                                    <ul
                                                                        className={
                                                                            "mt-2 ml-4 list-disc text-base/6 text-zinc-500 sm:text-sm/6 dark:text-zinc-400"
                                                                        }
                                                                    >
                                                                        {getUsersInGroup(groupKey, testResult.users)}
                                                                    </ul>
                                                                </>
                                                            ) : undefined}
                                                        </>
                                                    </div>
                                                </motion.div>
                                            </motion.div>
                                        ))}
                                    </div>
                                )}
                                {testResult.users && (
                                    <div className={"flex w-full flex-col gap-3"}>
                                        <Subheading>{t("heading.users-found")}</Subheading>
                                        <motion.div
                                            layout
                                            transition={{
                                                duration: 0.3,
                                            }}
                                            onClick={() => setUsersOpen(!usersOpen)}
                                            className={
                                                "rounded border border-zinc-950/5 bg-zinc-400/10 p-3 dark:border-white/5"
                                            }
                                        >
                                            <motion.div layout className={"flex justify-between gap-3"}>
                                                <div className={"flex gap-3"}>
                                                    <div className={"w-5 *:fill-black/40 *:dark:fill-white/40"}>
                                                        <UsersIcon />
                                                    </div>
                                                    <Subheading>Users</Subheading>
                                                    <Badge color={"zinc"}>
                                                        {Object.entries(testResult.users).length}
                                                    </Badge>
                                                </div>

                                                <div className={"w-5 *:fill-black/40 *:dark:fill-white/40"}>
                                                    <ChevronDownIcon />
                                                </div>
                                            </motion.div>
                                            <motion.div layout>
                                                <div className={usersOpen ? "" : "hidden"}>
                                                    <ul
                                                        className={
                                                            "mt-2 list-disc text-base/6 text-zinc-500 sm:text-sm/6 dark:text-zinc-400"
                                                        }
                                                    >
                                                        {Object.entries(testResult.users).map(([key, value]) => (
                                                            <div className={"flex gap-3"}>
                                                                {!pendingSettings?.membership_in_group ? (
                                                                    value?.list.length === 0 ? (
                                                                        <div
                                                                            title={"user is in no group"}
                                                                            className={"w-5 *:fill-yellow-400/80"}
                                                                        >
                                                                            <ExclamationTriangleIcon />
                                                                        </div>
                                                                    ) : undefined
                                                                ) : userInNumberOfGroups(key, testResult.groups) ===
                                                                  0 ? (
                                                                    <div
                                                                        title={"user is in no group"}
                                                                        className={"w-5 *:fill-yellow-400/80"}
                                                                    >
                                                                        <ExclamationTriangleIcon />
                                                                    </div>
                                                                ) : undefined}
                                                                <li className={"ml-4"}>{key}</li>
                                                            </div>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </motion.div>
                                        </motion.div>
                                    </div>
                                )}
                                <PrimaryButton className={"w-fit"} onClick={saveSettings}>
                                    {t("button.save-with-settings")}
                                </PrimaryButton>
                            </div>
                        )}
                        {testResult.type === "Error" && (
                            <div className={"flex flex-col gap-3"}>
                                <Textarea
                                    disabled={true}
                                    resizable={false}
                                    className={
                                        "h-[20em] w-full disabled:border-zinc-950/10 has-data-disabled:opacity-100 has-data-disabled:before:bg-white dark:disabled:border-white/10 dark:disabled:bg-white/5"
                                    }
                                    value={testResult.message}
                                />
                                <Text>{t("description.change-settings")}</Text>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export const Route = createFileRoute("/_admin/a/settings/_settings/ldap")({
    component: SettingLdap,
    loader: async () => {
        return await ApiInternal.ldap.getSettings();
    },
});
